import { Component, ElementRef, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { VexPopoverService } from '@vex/components/vex-popover/vex-popover.service';
import { ToolbarNotificationsDropdownComponent } from './toolbar-notifications-dropdown/toolbar-notifications-dropdown.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { LoadNotifications, UpdateUnreadCount } from './state/notifications.state';
import { NotificationsState } from './state/notifications.state';
import { AsyncPipe } from '@angular/common';
import { NotificationCoordinationService } from './services/notification-coordination.service';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'vex-toolbar-notifications',
    templateUrl: './toolbar-notifications.component.html',
    styleUrls: ['./toolbar-notifications.component.scss'],
    standalone: true,
    imports: [MatButtonModule, MatIconModule, AsyncPipe],
})
export class ToolbarNotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('originRef', { static: true, read: ElementRef })
    originRef?: ElementRef;

    unreadCount$: Observable<number>;
    dropdownOpen = false;
    private destroy$ = new Subject<void>();

    constructor(
        private popover: VexPopoverService,
        private store: Store,
        private notificationCoordination: NotificationCoordinationService,
    ) {
        this.unreadCount$ = this.store.select(NotificationsState.getUnreadCount);
    }

    ngOnInit(): void {
        this.setupWebPushNotifications();
        // Load initial notifications to ensure state is populated
        // This also calculates the unread count locally, so no need for separate loadUnreadCount() call
        this.store.dispatch(new LoadNotifications(false));

        // Listen for new notification events to trigger updates
        this.notificationCoordination.newNotification$
            .pipe(takeUntil(this.destroy$))
            .subscribe((hasNewNotification) => {
                if (hasNewNotification) {
                    // Don't call UpdateUnreadCount API here as AddRealTimeNotification already updated the local count
                    // Only reset the flag to avoid multiple triggers
                    this.notificationCoordination.resetNotificationFlag();
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    showPopover() {
        // Load notifications when opening dropdown
        this.store.dispatch(new LoadNotifications(true));

        this.dropdownOpen = true;

        if (!this.originRef) {
            throw new Error('originRef undefined!');
        }

        const popoverRef = this.popover.open({
            content: ToolbarNotificationsDropdownComponent,
            origin: this.originRef,
            offsetY: 12,
            position: [
                {
                    originX: 'center',
                    originY: 'top',
                    overlayX: 'center',
                    overlayY: 'bottom',
                },
                {
                    originX: 'end',
                    originY: 'bottom',
                    overlayX: 'end',
                    overlayY: 'top',
                },
            ],
        });

        popoverRef.afterClosed$.subscribe(() => {
            this.dropdownOpen = false;
        });
    }

    private loadUnreadCount(): void {
        this.store.dispatch(new UpdateUnreadCount());
    }

    private setupWebPushNotifications(): void {
        // Real-time notification handling is managed by WebPushComponent
        // This method is kept for future extensions if needed
    }
}
